import { NgModule } from "@angular/core";
import { StoreModule, MetaReducer } from "@ngrx/store";
import { EffectsModule } from "@ngrx/effects";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { Stores } from "./app-store.state";
import { environment } from "@env/environment";
import { validationMetaReducer } from "./pages/quote/validation/validation.meta-reducer";
import { quoteStoreSyncMetaReducer } from "src/pdf/store/quote-store-sync.meta-reducer";

// **************************************************************************
// APPLICATION - Infrastructure (auth, clipboard, data, settings, notification)
// **************************************************************************
import { applicationReducer } from "./application/application.reducer";
import { AuthenticationEffects } from "./application/authentication/authentication.effects";
import { ClipboardEffects } from "./application/clipboard/clipboard.effects";
import { DataEffects as CoreDataEffects } from "./application/data/data.effects";
import { CreditCheckEffects } from "./application/data/credit-check/credit-check.effects";
import { NotificationEffects } from "./application/notification/notification.effects";

// **************************************************************************
// USER - User context (profile, companies, roles)
// **************************************************************************
import { userReducer } from "./user/user.reducer";
import { UserEffects } from "./user/user.effects";

// **************************************************************************
// NAVIGATION - Standalone UI navigation state (sidebar, aside, settings drawer)
// **************************************************************************
import { navigationReducer } from "./navigation/navigation.reducer";
import { NavigationEffects } from "./navigation/navigation.effects";

// **************************************************************************
// FEEDBACK - UI feedback: toasts, progress dialogs, success dialogs
// **************************************************************************
import { FeedbackEffects } from "./application/feedback/feedback.effects";

// **************************************************************************
// PAGES - Consolidated page-level state (quote, customer, contract, etc.)
// **************************************************************************
import { pagesReducer } from "./pages/pages.reducer";

// Quote Effects (consolidated)
import { QuoteEffects } from "./pages/quote/quote.effects";
import { QuoteVersionEffects } from "./pages/quote/version/version.effects";
import { ArticlesEffects } from "./pages/quote/articles/articles.effects";
import { VendorInquiriesEffects } from "./pages/quote/vendor-inquiries/vendor-inquiries.effects";
import { DrawingEffects } from "./pages/quote/drawing/drawing.effects";
// Preview, Delivery, Billing, Notes, QuoteInfo → consolidated into QuoteEffects
// ProductCatalog → consolidated into ArticlesEffects
import { QuotesEffects } from "./pages/quote/quotes/quotes.effects";

// Quote Create - Effects for creating new quotes (reducers are in pages.reducer.ts)
import { QuoteCreateEffects } from "./pages/quote-create/quote/quote-create.effects";
import { QuoteSubscriptionCreateEffects } from "./pages/quote-create/quote-subscription/quote-subscription-create.effects";

// Customer Effects
import { CustomerEffects } from "./pages/customer/customer.effects";
import { CustomersEffects } from "./pages/customer/customers/customers.effects";

// Contact Effects
import { ContactEffects } from "./pages/contact/contact.effects";
import { ContactsEffects } from "./pages/contact/contacts/contacts.effects";

// Contract Effects
import { ContractEffects } from "./pages/contract/contract.effects";
import { ContractsEffects as ContractListEffects } from "./pages/contract/contracts/contracts.effects";

// Catalog Effects
import { CatalogEffects } from "./pages/catalog/catalog.effects";
import { CatalogsEffects as CatalogListEffects } from "./pages/catalog/catalogs/catalogs.effects";

// Vendor Effects
import { VendorsEffects } from "./pages/vendor/vendors/vendors.effects";

// Sales Order Effects
import { SalesOrderEffects } from "./pages/sales-order/sales-order.effects";
import { SalesOrdersEffects } from "./pages/sales-order/sales-orders/sales-orders.effects";

// Admin Effects
import { ReviewQuotesEffects } from "./pages/admin/central-support/review-quotes/review-quotes.effects";
import { TransferQuotesEffects } from "./pages/admin/developer/transfer-quotes/transfer-quotes.effects";
import { AutoArchiveEffects } from "./pages/admin/developer/auto-archive/auto-archive.effects";
import { InactiveCustomersEffects } from "./pages/admin/developer/inactive-customers/inactive-customers.effects";
import { SendReminderEffects } from "./pages/admin/developer/send-reminder/send-reminder.effects";
import { EmailLogsEffects } from "./pages/admin/developer/email-logs/email-logs.effects";
import { WebhooksEffects } from "./pages/admin/developer/webhooks/webhooks.effects";
import { AttioQuoteMigrationEffects } from "./pages/admin/developer/attio-quote-migration/attio-quote-migration.effects";
import { AttioRecordMigrationEffects } from "./pages/admin/developer/attio-record-migration/attio-record-migration.effects";
import { CurrenciesEffects } from "./pages/admin/super-user/currencies/currencies.effects";
import { InterestRatesEffects } from "./pages/admin/super-user/interest-rates/interest-rates.effects";
import { PaymentTermsEffects } from "./pages/admin/super-user/payment-terms/payment-terms.effects";
import { DeliveryTermsEffects } from "./pages/admin/super-user/delivery-terms/delivery-terms.effects";
import { SalesCreditRolesEffects } from "./pages/admin/super-user/sales-credit-roles/sales-credit-roles.effects";
import { CompaniesEffects } from "./pages/admin/super-user/companies/companies.effects";
import { FinancingCompaniesEffects } from "./pages/admin/super-user/financing-companies/financing-companies.effects";
import { ExternalUsersEffects } from "./pages/admin/super-user/users/external-users/external-users.effects";
import { InternalUsersEffects } from "./pages/admin/super-user/users/internal-users/internal-users.effects";
import { CountriesEffects } from "./pages/admin/super-user/countries/countries.effects";
import { QuoteSearchEffects } from "./pages/admin/super-user/quote-search/quote-search.effects";
import { CatalogManagementEffects } from "./pages/admin/contract-admin/catalog-management/catalog-management.effects";
import { ContractManagementEffects } from "./pages/admin/contract-admin/contract-management/contract-management.effects";
import { ContractReportsEffects } from "./pages/admin/contract-admin/contract-reports/contract-reports.effects";
import { CatalogDatasetsEffects } from "./pages/admin/contract-admin/catalog-datasets/catalog-datasets.effects";

// **************************************************************************
// PDF - Anonymous PDF generation (quote, vendor-inquiry)
// **************************************************************************
import { pdfReducer } from "src/pdf/store/pdf.reducer";
import { PdfEffects } from "src/pdf/store/pdf.effects";

// Meta-reducers run before feature reducers
const metaReducers: MetaReducer[] = [validationMetaReducer, quoteStoreSyncMetaReducer];

@NgModule({
    imports: [
        StoreModule.forRoot({}, { metaReducers }),
        EffectsModule.forRoot([]),

        // =====================================================================
        // APPLICATION - Infrastructure (auth, clipboard, data, settings, notification)
        // =====================================================================
        StoreModule.forFeature(Stores.Application, applicationReducer),
        EffectsModule.forFeature([AuthenticationEffects, ClipboardEffects, CoreDataEffects, CreditCheckEffects, NotificationEffects]),

        // =====================================================================
        // NAVIGATION - UI navigation state (sidebar, aside, settings drawer)
        // =====================================================================
        StoreModule.forFeature(Stores.Navigation, navigationReducer),
        EffectsModule.forFeature([NavigationEffects]),

        // =====================================================================
        // USER - User context (profile, companies, roles)
        // =====================================================================
        StoreModule.forFeature(Stores.User, userReducer),
        EffectsModule.forFeature([UserEffects]),

        // =====================================================================
        // FEEDBACK - UI feedback: toasts, progress dialogs, success dialogs
        // =====================================================================
        EffectsModule.forFeature([FeedbackEffects]),

        // =====================================================================
        // PAGES - All page-level state (quote, customer, contract, catalog, etc.)
        // =====================================================================
        StoreModule.forFeature(Stores.Pages, pagesReducer),
        EffectsModule.forFeature([
            // Quote (consolidated: QuoteEffects handles preview, delivery, billing, notes)
            // (QuoteVersionEffects handles discount, kickback, management, summary, brand-info)
            QuoteEffects,
            QuoteVersionEffects,
            ArticlesEffects,
            VendorInquiriesEffects,
            DrawingEffects,
            QuotesEffects,
            // Quote Create
            QuoteCreateEffects,
            QuoteSubscriptionCreateEffects,
            // Customer
            CustomerEffects,
            CustomersEffects,
            // Contact
            ContactEffects,
            ContactsEffects,
            // Contract
            ContractEffects,
            ContractListEffects,
            // Catalog
            CatalogEffects,
            CatalogListEffects,
            // Vendor
            VendorsEffects,
            // Sales Order
            SalesOrderEffects,
            SalesOrdersEffects,
            // Admin - Central Support
            ReviewQuotesEffects,
            // Admin - Developer
            TransferQuotesEffects,
            AutoArchiveEffects,
            InactiveCustomersEffects,
            SendReminderEffects,
            EmailLogsEffects,
            WebhooksEffects,
            AttioQuoteMigrationEffects,
            AttioRecordMigrationEffects,
            // Admin - Super User
            CurrenciesEffects,
            InterestRatesEffects,
            PaymentTermsEffects,
            DeliveryTermsEffects,
            SalesCreditRolesEffects,
            CompaniesEffects,
            FinancingCompaniesEffects,
            ExternalUsersEffects,
            InternalUsersEffects,
            CountriesEffects,
            QuoteSearchEffects,
            // Admin - Contract Admin
            CatalogManagementEffects,
            ContractManagementEffects,
            ContractReportsEffects,
            CatalogDatasetsEffects,
        ]),

        // =====================================================================
        // PDF - Anonymous PDF generation (quote, vendor-inquiry)
        // =====================================================================
        StoreModule.forFeature(Stores.Pdf, pdfReducer),
        EffectsModule.forFeature([PdfEffects]),
    ],

    providers: [
        environment.enableStoreDevtools
            ? [
                provideStoreDevtools({
                    maxAge: environment.devtoolsMaxAge,
                    logOnly: environment.devtoolsLogOnly,
                }),
            ]
            : [],
    ],
})
export class AppStoreModule { }
